import CheckBox from '@lib/components/CheckBox';
import ErrorMessage from '@lib/components/ErrorMessage';
import Selector from '@lib/components/Selector';
import TitleBar, * as ITitleBar from '@lib/components/TitleBar';
import { ThermostatProps } from '@lib/containers/Thermostat';
import { INativeNavigator, ITempChannel } from '@lib/types';
import { diffBetweenObject } from '@lib/util/util';
import * as React from 'react';
import './Thermostat.css';




interface IThermostatState {
  idDevice: string;
  temp: ITempChannel;
  selectorShow: boolean;
  selectorProperty: string;
  selectorValue: number;
  selectorStep: number;
  selectorMin: number;
  selectorMax: number;
  selectorLabel: string;
  modified: boolean;
}

class Thermostat extends React.Component<ThermostatProps, IThermostatState> {

  public constructor(props: ThermostatProps) {
    super(props);
    this.state = {
      idDevice: this.props.idDevice,
      modified: false,
      selectorLabel: '', selectorMax: 0, selectorMin: 0, selectorProperty: '', selectorShow: false, selectorStep: 0, selectorValue: 0,
      temp: JSON.parse(JSON.stringify(this.props.temp || {})),
    };
  }

  /**
   * Repercute changes on the fly
   * @param state 
   * @param selectorShow 
   */
  public onChangeFromSelector(state: IThermostatState, selectorShow: boolean) {
    if (this.state.selectorProperty === '' || this.state.selectorProperty === undefined) {
      this.setState({ selectorShow: false });
      return;
    }
    const tmp = this.state.temp;
    tmp[this.state.selectorProperty] = Math.round(this.state.selectorValue * 10) / 10;
    this.setState({ selectorShow, temp: { ...state.temp, tmp, modified: true, } });

  }


  /**
   * Fired when user clicks on "Close" in selector
   * @param state 
   * @param event 
   */
  public onSelectorCloseClick(state: IThermostatState, event: React.MouseEvent<HTMLDivElement>) {
    this.onChangeFromSelector(state, false);
  }

  /**
   * Fired when user clicks on "Up" in selector
   * @param state 
   * @param event 
   */
  public onInputClick(property: string, min: number, max: number, step: number, event: React.MouseEvent<HTMLInputElement>) {
    this.setState({
      selectorLabel: this.props.i18n.Thermostat[property + 'Short'],
      selectorMax: max,
      selectorMin: min,
      selectorProperty: property,
      selectorShow: true,
      selectorStep: step,
      selectorValue: this.state.temp[property],
    });
  }


  /**
   * Fired when user clicks on "Up" in selector
   * @param state 
   * @param event 
   */
  public onModeClick(newMode: number, event: React.MouseEvent<HTMLInputElement>) {
    const tmp = this.state.temp;

    // Check if there is schedule on the temp
    let hasSchedule = false;
    for (const schedule of this.props.site.thermostatSchedule!) {
      if (schedule.channel === this.state.temp.no.toString()) {
        hasSchedule = true;
        break;
      }
    }

    if (newMode === 4 && !hasSchedule) {
      // Planning asked but no schedule
      /* tslint:disable:no-empty */
      (navigator as INativeNavigator).notification.alert(this.props.i18n.Thermostat.planningNotAvailable, () => { }, 'Smart TX');
      return;
    }

    if (newMode === 0 && this.state.temp.thermostatMode === 0) {
      // Enabling the thermostat
      newMode = hasSchedule ? 4 : 2;
    }
    tmp.thermostatMode = newMode;
    this.setState({ selectorShow: false, temp: { ...this.state.temp, tmp }, modified: true });
  }


  /**
   * Fired when user click on menu hamburger button
   * @param event 
   */
  public onMenuClick(event: React.MouseEvent<HTMLDivElement>) {
    if (!this.state.modified || confirm(this.props.i18n.Common.confirmCancel)) {
      this.props.onGoBack();
    }
  }

  /**
   * Fired when user click on save button
   * @param event 
   */
  public onSaveClick(event: React.MouseEvent<HTMLDivElement>) {

    // Check temperatures thresehold
    if (this.state.temp.setpointComfort <= this.state.temp.setpointEco ||
      this.state.temp.setpointEco <= this.state.temp.setpointFrost) {
      /* tslint:disable:no-empty */
      (navigator as INativeNavigator).notification.alert(this.props.i18n.Thermostat.setpointOrderError, () => { }, 'Smart TX');
      return;
    }

    // Only take different fields
    const channelWithChange = diffBetweenObject(this.props.temp, this.state.temp);
    this.props.onSaveThermostat(this.state.idDevice, this.props.site!.name, channelWithChange);
  }

  /**
   * Fired when user wants to go to planning
   * @param event 
   */
  public onGoPlanning(event: React.MouseEvent<HTMLDivElement>) {
    window.location.hash = 'planning';
  }

  /**
   * Fired when slider is moved
   * @param state 
   * @param value 
   */
  public onSelectorChange(state: IThermostatState, value: number) {
    this.setState({ selectorValue: value, modified: true, }, () => {
      this.onChangeFromSelector(this.state, true);
    });
  }

  /**
   * Render React method
   */
  public render() {

    const i18n = this.props.i18n;

    if (this.props.temp === undefined || this.props.temp.setpointComfort === undefined) {
      window.location.pathname = '/';
      window.location.hash = '';
      return null;
    };

    return (
      <div className="standardPage thermostatPage">
        <TitleBar
          leftIcon={ITitleBar.ICO_CHEVRON_LEFT}
          onLeftButtonClick={this.onMenuClick.bind(this, this.state)}
          title={this.props.i18n.Thermostat.title + ' - ' + this.state.temp.label}
          rightIcon={this.props.saving ? ITitleBar.ICO_CHECK_ROTATING : ITitleBar.ICO_CHECK}
          onRightButtonClick={this.onSaveClick.bind(this, this.state)}
        />

        <Selector label={this.state.selectorLabel}
          maxValue={this.state.selectorMax}
          minValue={this.state.selectorMin}
          step={this.state.selectorStep}
          onCloseClick={this.onSelectorCloseClick.bind(this, this.state)}
          onChange={this.onSelectorChange.bind(this, this.state)}
          show={this.state.selectorShow} value={this.state.selectorValue}
          unit='°C' />

        <ErrorMessage message={i18n.Common.errors[this.props.errorMessage]} />
        <div className="contentCentered">
          {/* Off */}
          <div className="rowSetpoint">
            <CheckBox
              checked={this.state.temp.thermostatMode !== 0} label={this.props.i18n.Thermostat.modeOff} onClick={this.onModeClick.bind(this, 0)} />
            <div
              onClick={this.onModeClick.bind(this, 0)} />
          </div>
          {/* Comfort */}
          {this.state.temp.thermostatMode !== 0 &&
            <div className="rowSetpoint">
              <div className={'setpointName comfort' + (this.state.temp.thermostatMode === 2 ? 'Selected' : '')}
                onClick={this.onModeClick.bind(this, 2)} >
                <span>{this.props.i18n.Thermostat.modeComfort}</span>
              </div>
              <div className={'setpointValue ' + (this.state.temp.setpointComfort <= this.state.temp.setpointEco ? 'setpointValueError' : '')} onClick={this.onInputClick.bind(this, 'setpointComfort', this.state.temp.setpointEco + 0.5, 60, 0.5)} >
                {this.state.temp.setpointComfort.toFixed(1)}°C
            </div>
            </div>
          }
          {/* Economy */}
          {this.state.temp.thermostatMode !== 0 &&
            <div className="rowSetpoint">
              <div className={'setpointName eco' + (this.state.temp.thermostatMode === 1 ? 'Selected' : '')}
                onClick={this.onModeClick.bind(this, 1)} >
                <span>{this.props.i18n.Thermostat.modeEco}</span>
              </div>
              <div className={'setpointValue ' + ((this.state.temp.setpointEco <= this.state.temp.setpointFrost ||
                this.state.temp.setpointEco >= this.state.temp.setpointComfort) ? 'setpointValueError' : '')} onClick={this.onInputClick.bind(this, 'setpointEco', this.state.temp.setpointFrost + 0.5, this.state.temp.setpointComfort - 0.5, 0.5)}>
                {this.state.temp.setpointEco.toFixed(1)}°C
            </div>
            </div>
          }
          {/* Forst */}
          {this.state.temp.thermostatMode !== 0 &&
            <div className="rowSetpoint">
              <div className={'setpointName frost' + (this.state.temp.thermostatMode === 3 ? 'Selected' : '')}
                onClick={this.onModeClick.bind(this, 3)} >
                <span>{this.props.i18n.Thermostat.modeFrost}</span>
              </div>
              <div className={'setpointValue ' + (this.state.temp.setpointEco <= this.state.temp.setpointFrost ? 'setpointValueError' : '')} onClick={this.onInputClick.bind(this, 'setpointFrost', -30, this.state.temp.setpointEco - 0.5, 0.5)}>
                {this.state.temp.setpointFrost.toFixed(1)}°C
            </div>
            </div>
          }
          {/* Planning */}
          {this.state.temp.thermostatMode !== 0 &&
            <div className="rowSetpoint">
              <div className={'setpointName planning' + (this.state.temp.thermostatMode === 4 ? 'Selected' : '')}
                onClick={this.onModeClick.bind(this, 4)} >
                <span>{this.props.i18n.Thermostat.modePlanning}</span>
              </div>
              <div className="setpointArrow" onClick={this.onGoPlanning} />
            </div>
          }
        </div>


      </div >

    );
  }
}

export default Thermostat;


// WEBPACK FOOTER //
// ./src/pages/Thermostat.tsx